import React from 'react';
import { formatNumber } from '../../../../../features/dashboard/dashboardUtils';

/**
 * The tooltip every chart on this page uses.
 *
 * Recharts' own takes a `contentStyle` object, and the donut used to pass one
 * without a `backgroundColor` — so it inherited white and, in dark mode, drew
 * white-on-white. Rendering it as a real component instead lets Tailwind handle
 * both themes the way the rest of the app does.
 *
 * @param {object} props - Recharts passes `active`, `payload`, `label`.
 * @param {string} [props.title] - Overrides the label, for the donut where the
 *   slice name is the whole story.
 * @param {boolean} [props.showTotal] - Adds a summed row, for stacked charts.
 */
export const ChartTooltip = ({ active, payload, label, title, showTotal = false }) => {
  if (!active || !payload?.length) return null;

  const visible = payload.filter((entry) => entry.value > 0);
  if (visible.length === 0) return null;

  const total = visible.reduce((sum, entry) => sum + entry.value, 0);

  return (
    <div className="bg-white dark:bg-[#2d2d2d] border border-border rounded-xl shadow-md px-3 py-2 text-xs">
      {(title || label) && (
        <p className="font-bold text-ninja-dark dark:text-white mb-1.5">{title || label}</p>
      )}

      <div className="flex flex-col gap-1">
        {visible.map((entry) => (
          <div key={entry.name} className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: entry.color || entry.payload?.color }}
            />
            <span className="text-text-secondary">{entry.name}</span>
            <span className="ml-auto font-semibold text-ninja-dark dark:text-white">
              {formatNumber(entry.value)}
            </span>
          </div>
        ))}
      </div>

      {showTotal && visible.length > 1 && (
        <div className="flex items-center gap-2 mt-1.5 pt-1.5 border-t border-border">
          <span className="text-text-secondary">Total</span>
          <span className="ml-auto font-bold text-ninja-dark dark:text-white">
            {formatNumber(total)}
          </span>
        </div>
      )}
    </div>
  );
};
